/**
 * Privacy notice for AskTogether.
 *
 * Required under UK GDPR Articles 13/14 because the service processes
 * personal data — email addresses for organisers/operators/moderators
 * (via Google OAuth), display names for attendees, and the text of
 * any questions submitted.
 *
 * Patterned on joltstream's privacy notice (data-inventory table +
 * statutory disclosure). Substantive points: no analytics, no
 * advertising, no third-party trackers, no SMTP/email broadcasts; the
 * tool is single-purpose and the inventory below is the entire data
 * surface.
 *
 * Update LAST_UPDATED and the version history when substantive
 * changes land.
 */
const LAST_UPDATED = "2026-05-21";

export default function PrivacyPage() {
  return (
    <main className="prose">
      <p style={{ marginBottom: "0.5rem" }}>
        <a href="/join">← Back</a>
      </p>
      <h1>Privacy notice</h1>
      <p className="meta">Last updated: {LAST_UPDATED}</p>

      <h2>Who we are</h2>
      <p>
        AskTogether is operated by <strong>Time Back Solutions Ltd</strong>,
        a company registered in England and Wales. We are the data
        controller for any personal data processed by the service.
      </p>
      <p>
        ICO registration: <strong>application pending</strong>. We update
        this notice once the registration is listed on the public ICO
        register.
      </p>

      <h2>What we collect</h2>
      <p>
        Each row below is a piece of information AskTogether handles,
        with what it's used for, where it's kept, and how long.
      </p>
      <table className="data-inventory">
        <thead>
          <tr>
            <th>Data</th>
            <th>Purpose</th>
            <th>Where stored</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Organiser / operator / moderator email address (via Google
              OAuth)
            </td>
            <td>
              Authenticate you, scope sessions you can see, attribute audit
              actions
            </td>
            <td>Our database (Postgres in our infrastructure)</td>
            <td>
              For the lifetime of your role in an org; deletable on request
            </td>
          </tr>
          <tr>
            <td>Display name typed when joining a session</td>
            <td>Show who asked or upvoted a question</td>
            <td>Our database, alongside the session</td>
            <td>For the lifetime of the session</td>
          </tr>
          <tr>
            <td>
              Anonymous join (you tick &ldquo;Join anonymously&rdquo;)
            </td>
            <td>
              Lets you participate without a name being visible to the
              room; an internal participant id is still generated so we can
              count votes
            </td>
            <td>Our database, alongside the session</td>
            <td>For the lifetime of the session</td>
          </tr>
          <tr>
            <td>Question text you submit</td>
            <td>
              Show the question to the moderator and (once approved) to the
              room; cluster with similar questions
            </td>
            <td>
              Our database; an embedding vector is also stored in our
              private vector database, keyed by session so it can&apos;t
              influence other sessions
            </td>
            <td>For the lifetime of the session</td>
          </tr>
          <tr>
            <td>Upvotes &amp; &ldquo;add my name&rdquo; signals</td>
            <td>Rank the board, surface room interest</td>
            <td>Our database</td>
            <td>For the lifetime of the session</td>
          </tr>
          <tr>
            <td>Audit log entries (who did what, when)</td>
            <td>
              Operational accountability — who approved or rejected which
              question, who added a moderator, etc.
            </td>
            <td>
              Our shared audit service, keyed by org and session
            </td>
            <td>Retained with the session for accountability purposes</td>
          </tr>
          <tr>
            <td>OAuth session cookie</td>
            <td>
              Keep you signed in to the dashboard
              (asktogether.downloadserver.co.uk only — the public visitor
              host does not set this cookie)
            </td>
            <td>Browser, scoped to downloadserver.co.uk</td>
            <td>Until you sign out or the session expires</td>
          </tr>
          <tr>
            <td>Attendee session token</td>
            <td>
              Keep you joined to a session across page refreshes
            </td>
            <td>Browser sessionStorage (cleared when you close the tab)</td>
            <td>Until the tab closes</td>
          </tr>
        </tbody>
      </table>

      <h2>What we don&apos;t do</h2>
      <ul>
        <li>
          No advertising, no analytics, no third-party trackers. No
          Google Analytics, no pixel, no fingerprinting.
        </li>
        <li>
          No email broadcasts. Invite links are copy-pasted by the inviter
          manually; we don&apos;t send mail on your behalf.
        </li>
        <li>
          No cross-session matching. The vector store filters strictly by
          session id, so questions from one event never affect any other.
        </li>
        <li>
          No sharing with third parties beyond the sub-processors listed
          below.
        </li>
      </ul>

      <h2>Lawful basis</h2>
      <ul>
        <li>
          <strong>Legitimate interest</strong> — running an attendee Q&amp;A
          on behalf of an organiser is the substance of the product. The
          data we process is the minimum needed to do that.
        </li>
        <li>
          <strong>Consent</strong> — for any optional cookies / browser
          storage that isn&apos;t strictly necessary to deliver the
          service. See the <a href="/cookies">cookies page</a> for the
          full breakdown.
        </li>
      </ul>

      <h2>Sub-processors</h2>
      <ul>
        <li>
          <strong>Google</strong> — sign-in (Google OAuth) for organisers,
          operators, and moderators. Google receives only the OAuth flow
          itself; it does not see questions, votes, or session content.
        </li>
        <li>
          <strong>Cloudflare</strong> — DNS, TLS termination, and DDoS
          protection. Cloudflare sees request metadata (URL, IP) but no
          stored application data.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>Under UK GDPR you have the right to:</p>
      <ul>
        <li>access the personal data we hold about you;</li>
        <li>have inaccuracies corrected;</li>
        <li>
          have data erased — note that questions you submitted to a session
          may have been moderated and shown to other attendees; we can
          remove your identifying data but cannot retroactively un-show
          the question;
        </li>
        <li>restrict or object to processing;</li>
        <li>data portability (export the data we hold).</li>
      </ul>
      <p>
        Contact us at{" "}
        <a href="mailto:privacy@asktogether.co.uk">privacy@asktogether.co.uk</a>{" "}
        with any data-subject request. You also have the right to
        complain to the{" "}
        <a
          href="https://ico.org.uk/make-a-complaint/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Information Commissioner&apos;s Office
        </a>
        .
      </p>

      <h2>Changes</h2>
      <p>
        Substantive changes update the <em>Last updated</em> date at the
        top of this page. We don&apos;t notify by email (no SMTP) — if you
        rely on this notice, re-read it occasionally.
      </p>
    </main>
  );
}
